<template>
  <div>
    <div v-html="parseMarkdown(markdownText)" class="ai-chat-markdown-content" />

    <div v-if="docContent && docContent.sourceUrls" class="mt-2">
      <div class="d-flex flex-column ga-2">
        <a
          v-for="(source, idx) in docContent.sourceUrls"
          :key="idx"
          :href="source.url"
          target="_blank"
          rel="noopener noreferrer"
          class="documentation-link d-flex align-center px-2 py-1"
        >
          <v-icon size="16" icon="$link" class="mr-2" />
          <span class="body3">{{ source.label }}</span>
        </a>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { parseMarkdown } from "@/_helpers/markdown";
import type { DocumentationSearchContent } from "@/_store/ai-assistant.module";

export default defineComponent({
  props: {
    markdownText: {
      type: String,
      required: true,
    },
    docContent: {
      type: [Object, null] as PropType<DocumentationSearchContent | null>,
      default: null,
    },
  },
  setup() {
    return {
      parseMarkdown,
    };
  },
});
</script>

<style lang="scss" scoped>
.ai-chat-markdown-content {
  /* Allow long tokens (URLs, hashes) to wrap instead of forcing horizontal scroll. */
  overflow-wrap: break-word;
  /* Keep break-word for older engines that don't fully honor overflow-wrap. */
  word-break: break-word;

  :deep(p) {
    margin: 0 0 8px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
  :deep(ul),
  :deep(ol) {
    margin: 8px 0;
    padding-left: 20px;
  }
  :deep(li) {
    margin-bottom: 4px;
  }
  :deep(strong) {
    font-weight: 600;
  }
  :deep(code) {
    background-color: rgba(0, 0, 0, 0.1);
    padding: 2px 4px;
    border-radius: 3px;
    font-family: monospace;
    font-size: 0.9em;
  }
  :deep(pre) {
    background-color: rgba(0, 0, 0, 0.05);
    padding: 12px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 8px 0;
    code {
      background: none;
      padding: 0;
    }
  }
  :deep(a) {
    font-family: "Source Sans Pro", sans-serif;
    font-size: 12px;
    color: rgb(var(--v-theme-link));
    text-decoration: underline;
    font-weight: 400;
  }
}

.documentation-link {
  background-color: rgb(var(--v-theme-blue-light), 0.1);
  border-radius: 4px;
  width: fit-content;
  span {
    color: rgb(var(--v-theme-link));
  }
}
</style>
